import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const API_URL = '/api/dcp-jobs';

export const fetchDCPJobs = createAsyncThunk(
  'dcpJobs/fetchJobs',
  async ({ status, skip = 0, limit = 100, includeArchived = false } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL}/`, {
        params: { status, skip, limit, include_archived: includeArchived || undefined },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const restoreDCPJob = createAsyncThunk(
  'dcpJobs/restore',
  async (jobId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/${jobId}/restore`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchDCPJobStats = createAsyncThunk(
  'dcpJobs/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL}/stats`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchDCPAvailableFiles = createAsyncThunk(
  'dcpJobs/fetchAvailableFiles',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL}/available-files`);
      return data.files;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createDCPJob = createAsyncThunk(
  'dcpJobs/createJob',
  async (jobData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/`, jobData, {
        headers: { 'Content-Type': 'application/json' },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const cancelDCPJob = createAsyncThunk(
  'dcpJobs/cancel',
  async (jobId, { rejectWithValue }) => {
    try {
      await axios.post(`${API_URL}/${jobId}/cancel`, {});
      return jobId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteDCPJob = createAsyncThunk(
  'dcpJobs/delete',
  async (arg, { rejectWithValue }) => {
    // Accept either a bare jobId (legacy callers) or {jobId, force}.
    // The backend returns 409 if the DCP has active KDMs/deliveries
    // unless the caller passes force=true; surface the structured detail
    // so the UI can show a stronger confirmation.
    const jobId = (typeof arg === 'object' && arg !== null) ? arg.jobId : arg;
    const force = (typeof arg === 'object' && arg !== null) ? !!arg.force : false;
    try {
      await axios.delete(`${API_URL}/${jobId}`, { params: force ? { force: 'true' } : {} });
      return jobId;
    } catch (error) {
      // Pass through the full detail object on 409 so the caller can
      // distinguish "needs force confirmation" from real failures.
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object') {
        return rejectWithValue({ ...detail, status: 409 });
      }
      return rejectWithValue(detail || error.message);
    }
  }
);

export const restartDCPJob = createAsyncThunk(
  'dcpJobs/restart',
  async (jobId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/${jobId}/restart`, {});
      return { jobId, status: data.status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const renameDCPJob = createAsyncThunk(
  'dcpJobs/rename',
  async ({ jobId, name }, { rejectWithValue }) => {
    try {
      // 15s ceiling: a stuck DB pool wedged the rename for 30s once,
      // freezing the inline editor UI in saving=true. Failing fast lets
      // the form recover its error state instead of silently spinning.
      const { data } = await axios.patch(`${API_URL}/${jobId}`, { name }, { timeout: 15000 });
      return data;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        return rejectWithValue('Rename timed out — server is slow or unresponsive. Try again.');
      }
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const dcpJobsSlice = createSlice({
  name: 'dcpJobs',
  initialState: {
    jobs: [],
    availableFiles: [],
    stats: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearDCPJobsError: (state) => {
      state.error = null;
    },
    addDCPJob: (state, action) => {
      const idx = state.jobs.findIndex(j => j.id === action.payload.id);
      if (idx !== -1) {
        state.jobs[idx] = { ...state.jobs[idx], ...action.payload };
      } else {
        state.jobs.unshift(action.payload);
      }
    },
    updateDCPJobInList: (state, action) => {
      const index = state.jobs.findIndex(job => job.id === action.payload.id);
      if (index !== -1) {
        state.jobs[index] = { ...state.jobs[index], ...action.payload };
      }
    },
    removeDCPJob: (state, action) => {
      state.jobs = state.jobs.filter(job => job.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDCPJobs.pending, (state) => {
        if (state.jobs.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchDCPJobs.fulfilled, (state, action) => {
        state.loading = false;
        // Smart merge to prevent flicker
        const newJobs = action.payload;
        const existingJobIds = new Set(state.jobs.map(job => job.id));
        const newJobIds = new Set(newJobs.map(job => job.id));

        state.jobs.forEach((job, index) => {
          const newJob = newJobs.find(nj => nj.id === job.id);
          if (newJob) {
            state.jobs[index] = newJob;
          }
        });

        newJobs.forEach(newJob => {
          if (!existingJobIds.has(newJob.id)) {
            state.jobs.unshift(newJob);
          }
        });

        state.jobs = state.jobs.filter(job => newJobIds.has(job.id));
      })
      .addCase(fetchDCPJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDCPJobStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(fetchDCPAvailableFiles.fulfilled, (state, action) => {
        state.availableFiles = action.payload;
      })
      .addCase(fetchDCPAvailableFiles.rejected, (state) => {
        state.availableFiles = [];
      })
      .addCase(createDCPJob.fulfilled, (state, action) => {
        const idx = state.jobs.findIndex(j => j.id === action.payload.id);
        if (idx !== -1) {
          state.jobs[idx] = { ...state.jobs[idx], ...action.payload };
        } else {
          state.jobs.unshift(action.payload);
        }
      })
      .addCase(cancelDCPJob.fulfilled, (state, action) => {
        const job = state.jobs.find(j => j.id === action.payload);
        if (job) {
          job.status = 'cancelled';
        }
      })
      .addCase(deleteDCPJob.fulfilled, (state, action) => {
        state.jobs = state.jobs.filter(j => j.id !== action.payload);
      })
      .addCase(restartDCPJob.fulfilled, (state, action) => {
        const job = state.jobs.find(j => j.id === action.payload.jobId);
        if (job) {
          job.status = action.payload.status;
          job.progress = 0;
          job.error_message = null;
        }
      })
      .addCase(renameDCPJob.fulfilled, (state, action) => {
        const idx = state.jobs.findIndex(j => j.id === action.payload.id);
        if (idx >= 0) state.jobs[idx] = { ...state.jobs[idx], name: action.payload.name };
      });
  },
});

export const { clearDCPJobsError, addDCPJob, updateDCPJobInList, removeDCPJob } = dcpJobsSlice.actions;
export default dcpJobsSlice.reducer;
